import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { CartEntry, ValidatedCartItem } from "./types"
import { SELECTION_UNIT_PIECE, SELECTION_UNIT_KG, KG_QUANTITY_STEP } from "@/constants/misc"
import { trackAction } from "@/lib/track-action"

type CartState = {
  // Persisted
  entries: CartEntry[]
  outOfStockEntries: CartEntry[]
  // Transient
  validatedItems: ValidatedCartItem[]
  outOfStockValidatedItems: ValidatedCartItem[]
  isRefreshing: boolean
  lastRefreshedAt: number | null
}

type PriceInfo = {
  displayPrice: number | null
  pricePerPiece: number | null
  pricePerKg: number | null
  name?: string
  imageUrl?: string
}

type CartActions = {
  addItem: (productId: string, unit?: string, priceMeasurementUnit?: string, canBePurchasedByUnit?: boolean, canBePurchasedByKg?: boolean, priceInfo?: PriceInfo) => void
  removeItem: (productId: string) => void
  updateQuantity: (productId: string, qty: number) => void
  clearCart: () => void
  // Internal — called by refreshCart
  setValidatedItems: (items: ValidatedCartItem[]) => void
  setOutOfStockValidatedItems: (items: ValidatedCartItem[]) => void
  setIsRefreshing: (v: boolean) => void
  setLastRefreshedAt: (ts: number) => void
  reconcileStock: (availableIds: string[], unavailableIds: string[]) => void
}

export const useCartStore = create<CartState & CartActions>()(
  persist(
    (set, get) => ({
      // State
      entries: [],
      outOfStockEntries: [],
      validatedItems: [],
      outOfStockValidatedItems: [],
      isRefreshing: false,
      lastRefreshedAt: null,

      // Actions
      addItem: (productId, unit = SELECTION_UNIT_PIECE, priceMeasurementUnit = "", canBePurchasedByUnit = true, canBePurchasedByKg = false, priceInfo?) => {
        const isNew = !get().entries.find((e) => e.productId === productId)
        if (isNew) trackAction("ADD-TO-CART", productId)
        set((s) => {
          const step = unit === SELECTION_UNIT_KG ? KG_QUANTITY_STEP : 1
          const existing = s.entries.find((e) => e.productId === productId)
          if (existing) {
            const newQty = Math.round((existing.quantity + step) * 10) / 10
            return {
              entries: s.entries.map((e) =>
                e.productId === productId ? { ...e, quantity: newQty } : e
              ),
              validatedItems: s.validatedItems.map((v) =>
                v.productId === productId ? { ...v, quantity: newQty } : v
              ),
            }
          }
          // Seed a stub validatedItem so subtotal updates immediately
          const stubValidated: ValidatedCartItem | null = priceInfo
            ? {
                productId,
                name: priceInfo.name ?? "",
                price: priceInfo.pricePerPiece ?? priceInfo.pricePerKg ?? 0,
                displayPrice: priceInfo.displayPrice,
                pricePerKg: priceInfo.pricePerKg,
                pricePerPiece: priceInfo.pricePerPiece,
                imageUrl: priceInfo.imageUrl ?? "",
                quantity: step,
                available: true,
                maxQuantity: null,
                unit,
                priceMeasurementUnit,
                canBePurchasedByUnit,
                canBePurchasedByKg,
              }
            : null
          return {
            entries: [...s.entries, { productId, quantity: step, unit, priceMeasurementUnit, canBePurchasedByUnit, canBePurchasedByKg }],
            // If user re-adds from catalog, it's available — remove from out-of-stock
            outOfStockEntries: s.outOfStockEntries.filter((e) => e.productId !== productId),
            ...(stubValidated ? { validatedItems: [...s.validatedItems, stubValidated] } : {}),
          }
        })
        if (isNew && typeof fbq !== "undefined") {
          fbq("track", "AddToCart", {
            content_ids: [productId],
            content_name: priceInfo?.name,
            value: priceInfo?.pricePerPiece ?? priceInfo?.pricePerKg ?? 0,
            currency: "MXN",
          })
        }
      },

      removeItem: (productId) => {
        trackAction("REMOVE-FROM-CART", productId);
        set((s) => ({
          entries: s.entries.filter((e) => e.productId !== productId),
          outOfStockEntries: s.outOfStockEntries.filter((e) => e.productId !== productId),
          validatedItems: s.validatedItems.filter((v) => v.productId !== productId),
        }));
      },

      updateQuantity: (productId, qty) =>
        set((s) => {
          if (qty <= 0) {
            return {
              entries: s.entries.filter((e) => e.productId !== productId),
              outOfStockEntries: s.outOfStockEntries.filter((e) => e.productId !== productId),
              validatedItems: s.validatedItems.filter((v) => v.productId !== productId),
            }
          }
          return {
            entries: s.entries.map((e) =>
              e.productId === productId ? { ...e, quantity: qty } : e
            ),
            validatedItems: s.validatedItems.map((v) =>
              v.productId === productId ? { ...v, quantity: qty } : v
            ),
          }
        }),

      clearCart: () => set({ entries: [], outOfStockEntries: [], validatedItems: [], outOfStockValidatedItems: [], lastRefreshedAt: null }),

      setValidatedItems: (items) => set({ validatedItems: items }),
      setOutOfStockValidatedItems: (items) => set({ outOfStockValidatedItems: items }),
      setIsRefreshing: (v) => set({ isRefreshing: v }),
      setLastRefreshedAt: (ts) => set({ lastRefreshedAt: ts }),

      reconcileStock: (availableIds, unavailableIds) =>
        set((s) => {
          // Items currently in entries that went out of stock → move to outOfStockEntries
          const newlyUnavailable = s.entries.filter((e) => unavailableIds.includes(e.productId))
          // Items currently in outOfStockEntries that are back in stock → move to entries
          const newlyAvailable = s.outOfStockEntries.filter((e) => availableIds.includes(e.productId))

          return {
            entries: [
              ...s.entries.filter((e) => !unavailableIds.includes(e.productId)),
              ...newlyAvailable,
            ],
            outOfStockEntries: [
              ...s.outOfStockEntries.filter((e) => !availableIds.includes(e.productId)),
              ...newlyUnavailable,
            ],
          }
        }),
    }),
    {
      name: "calii-cart",
      partialize: (s) => ({ entries: s.entries, outOfStockEntries: s.outOfStockEntries }),
    }
  )
)

// Selectors
export const selectEntries = (s: CartState & CartActions) => s.entries
export const selectValidatedItems = (s: CartState & CartActions) => s.validatedItems
export const selectOutOfStockValidatedItems = (s: CartState & CartActions) => s.outOfStockValidatedItems
export const selectIsRefreshing = (s: CartState & CartActions) => s.isRefreshing
export const selectTotalItems = (s: CartState & CartActions) => s.entries.length
export const selectItemQuantity = (productId: string) => (s: CartState & CartActions) =>
  s.entries.find((e) => e.productId === productId)?.quantity ?? 0
